import { AdaaHelper } from "../../../../../../../core/utils";
import { Constants } from "../../../../../../constants/constants";
import { SharedFields, textField } from "../../configs";
import {
  AccordionSectionConfigType,
  ListSectionConfigType,
  ScreenSectionConfigType,
  ScreenType,
} from "../../wf-difference.types";
import {
  actualEndDateField,
  actualStartDateField,
  milestonesSectionConfig,
  subMilestonesSectionConfig,
} from "../tp-screen/milestones-config";

const cycleField = () => {
  const field = textField("cycle", { use: "current" }) as ScreenSectionConfigType;
  field.label = "annual_meetings.cycles.single";
  return field;
};
const projectField = () => {
  const field = textField("annualMeetingProjectName", { use: "current" }) as ScreenSectionConfigType;
  field.label = "annual_meetings.single";
  return field;
};

const allocatedBudgetField: ScreenSectionConfigType = {
  field: "budget",
  label: "initiatives.budget",
  view: "normal",
};

const expensesField: ScreenSectionConfigType = {
  field: "expense",
  label: "initiatives.expenses",
  view: "normal",
};

const progressField: ScreenSectionConfigType = {
  field: "progress",
  label: "box_status.activity_progress",
  view: "normal",
};

const signOffField: ScreenSectionConfigType = {
  field: "signOff",
  label: "national_projects.sign_off.justification",
  view: "normal",
};

const signOffAttachmentField: ScreenSectionConfigType = {
  field: "signOffAttachment",
  label: "national_projects.sign_off.evidence_wf_cs",
  view: "normal",
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  format: ($o) => ($o as any).signOffAttachment?.filename,
  hasDiff: ($newValue: unknown, $oldValue: unknown) => {
    const $n = $newValue as { id: number };
    const $o = $oldValue as { id: number };
    return $n?.id !== $o?.id;
  },
  isClickable: true,
};

const contributingEntitiesField: ListSectionConfigType = {
  field: "contributingEntities",
  label: "kpi.contributing_entities",
  view: "list",
  idKey: "entityId",
  derivedList: (data: unknown) => {
    const list = data as { otherEntityId?: number }[];
    return list.filter(($i) => !AdaaHelper.isDefined($i?.otherEntityId));
  },
  format: ($o) => AdaaHelper.getItemValueByToken($o, "entityName"),
};

const otherContributingEntitiesField: ListSectionConfigType = {
  field: "contributingEntities",
  label: "other_entities.contrib_entities",
  view: "list",
  idKey: "otherEntityId",
  derivedList: (data: unknown) => {
    const list = data as { otherEntityId?: number }[];
    return list.filter(($i) => AdaaHelper.isDefined($i?.otherEntityId));
  },
  format: ($o) => AdaaHelper.getItemValueByToken($o, "otherEntityName"),
};

const decisionsField: ListSectionConfigType = {
  field: "decisions",
  label: "national_projects.pmo_decisions",
  view: "list",
  idKey: "optionId",
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  format: ($o: any) => {
    let value = AdaaHelper.getItemValueByToken($o, "option");
    if ($o.optionId === Constants.CONSTANT_PMO_DECISION_OTHERS_ID) {
      value += ` (${$o.comment})`;
    }
    return value;
  },
  hasDiff: ($newValue: unknown, $oldValue: unknown) => {
    const $n = $newValue as { optionId?: number; comment?: string };
    const $o = $oldValue as { optionId?: number; comment?: string };
    if (
      $n.optionId === Constants.CONSTANT_PMO_DECISION_OTHERS_ID &&
      $o.optionId === Constants.CONSTANT_PMO_DECISION_OTHERS_ID
    ) {
      return $n.comment !== $o.comment;
    }

    return $n.optionId !== $o.optionId;
  },
};

const targetAudienceField: ListSectionConfigType = {
  field: "targetAudience",
  label: "national_projects.target_audience",
  view: "list",
  idKey: "id",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "audience"),
};

const expectedImpactField: ListSectionConfigType = {
  field: "expectedImpact",
  label: "national_projects.expected_impact",
  view: "list",
  idKey: "id",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "impact"),
};

const commentsField: ListSectionConfigType = {
  field: "comments",
  label: "national_projects.comments",
  view: "list",
  idKey: "id",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "comments"),
};

const projectMilestonesField: AccordionSectionConfigType = {
  view: "accordion",
  field: "milestones",
  title: ($o) => AdaaHelper.getItemValueByToken($o, "name"),
  idKey: "id",
  config: milestonesSectionConfig,
  label: "national_projects.national_projects_milestone.title",
  hasDiff: ($newValue: unknown, $oldValue: unknown) => {
    const $n = $newValue as Record<string, unknown>;
    const $o = $oldValue as Record<string, unknown>;
    const keys = [
      "id",
      "nameAE",
      "nameEN",
      "dscAE",
      "dscAE",
      "startDate",
      "endDate",
      "entityId",
      "otherEntityId",
      "owner",
      "budget",
      "milestoneOrder",
    ];

    return keys.some((key) => $o[key] !== $n[key]);
  },
};

export const linkedNationalStrategiesField: ScreenSectionConfigType = {
  view: "linked-national-strategies",
  field: "govDirections",
  label: "national_strategies.strategies",
};

export const agmProjectScreenConfig = {
  type: "kpi" as ScreenType,
  sections: [
    cycleField(),
    ...SharedFields.name,
    ...SharedFields.description,
    {
      ...SharedFields.startDate,
      label: "national_projects.planned_start_date",
    },
    {
      ...SharedFields.endDate,
      label: "national_projects.planned_end_date",
    },
    {
      ...SharedFields.entityIdField,
      label: "national_projects.accountable_entity",
      format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "accountableEntityName"),
    },
    SharedFields.orgUnitIdField,
    {
      ...SharedFields.ownerField,
      label: "national_projects.entity_lead",
    },
    {
      ...SharedFields.ownerField,
      label: "national_projects.project_coordinator",
      field: "projectCoordinator",
      format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "projectCoordinatorName"),
    },
    contributingEntitiesField,
    otherContributingEntitiesField,
    SharedFields.nationalSectorsField,
    decisionsField,
    targetAudienceField,
    expectedImpactField,
    commentsField,
    linkedNationalStrategiesField,
    projectMilestonesField,
    SharedFields.attachmentsField,
  ],
};

const tasksField: AccordionSectionConfigType = {
  view: "accordion",
  field: "tasks",
  label: "tasks.tasks",
  title: ($o) => AdaaHelper.getItemValueByToken($o, "name"),
  idKey: "id",
  config: subMilestonesSectionConfig,
  hasDiff: ($newValue: unknown, $oldValue: unknown) => {
    const $n = $newValue as Record<string, unknown>;
    const $o = $oldValue as Record<string, unknown>;
    const keys = [
      "id",
      "nameAE",
      "nameEN",
      "dscAE",
      "dscAE",
      "startDate",
      "endDate",
      "entityId",
      "otherEntityId",
      "complete",
    ];

    return keys.some((key) => $o[key] !== $n[key]);
  },
};

export const agmProjectMilestoneScreenConfig = {
  type: "kpi" as ScreenType,
  sections: [
    projectField(),
    ...SharedFields.name,
    ...SharedFields.description,
    {
      ...SharedFields.startDate,
      label: "national_projects.planned_start_date",
    },
    {
      ...SharedFields.endDate,
      label: "national_projects.planned_end_date",
    },
    actualStartDateField,
    actualEndDateField,
    {
      ...SharedFields.entityIdField,
      label: "national_projects.accountable_entity",
    },
    SharedFields.ownerField,
    contributingEntitiesField,
    otherContributingEntitiesField,
    allocatedBudgetField,
    expensesField,
    progressField,
    signOffField,
    signOffAttachmentField,
    SharedFields.nationalSectorsField,
    commentsField,
    linkedNationalStrategiesField,
    projectMilestonesField,
    SharedFields.attachmentsField,
    tasksField,
  ],
};
